import { Task, TaskPriority, TaskStatus } from '../../types';

/**
 * Extended Task type with student name derived from case
 */
export interface TaskWithCase extends Task {
  studentName: string;
}

/**
 * Filter tasks by completion status.
 * When showCompleted is false, excludes tasks with status 'completed'.
 * When showCompleted is true, returns all tasks.
 * 
 * Requirements: 1.1, 1.3, 1.4
 */
export function filterByCompleted(
  tasks: TaskWithCase[],
  showCompleted: boolean
): TaskWithCase[] {
  if (showCompleted) {
    return tasks;
  }
  return tasks.filter(task => task.status !== 'completed');
}

/**
 * Filter tasks by priority.
 * Returns only tasks whose priority is in the selected set.
 * If priorities is empty, returns all tasks.
 * 
 * Requirements: 3.1, 3.2
 */
export function filterByPriority(
  tasks: TaskWithCase[],
  priorities: TaskPriority[]
): TaskWithCase[] {
  if (priorities.length === 0) {
    return tasks;
  }
  return tasks.filter(task => priorities.includes(task.priority));
}

/**
 * Filter tasks by student (caseId).
 * Returns only tasks whose caseId is in the selected set.
 * If caseIds is empty, returns all tasks.
 * 
 * Requirements: 3.3
 */
export function filterByStudent(
  tasks: TaskWithCase[],
  caseIds: string[]
): TaskWithCase[] {
  if (caseIds.length === 0) {
    return tasks;
  }
  return tasks.filter(task => caseIds.includes(task.caseId));
}

/**
 * Filter configuration for combining multiple filters
 */
export interface TaskFilterConfig {
  showCompleted: boolean;
  priorities: TaskPriority[];
  caseIds: string[];
}

/**
 * Combine all filters and apply them to tasks.
 * Filters are applied in order: completed -> priority -> student
 * 
 * Requirements: 1.1, 1.3, 1.4, 3.1, 3.2, 3.3, 3.4
 */
export function combineFilters(
  tasks: TaskWithCase[],
  config: TaskFilterConfig
): TaskWithCase[] {
  let result = filterByCompleted(tasks, config.showCompleted);
  result = filterByPriority(result, config.priorities);
  result = filterByStudent(result, config.caseIds);
  return result;
}


/**
 * Sort direction type
 */
export type SortDirection = 'asc' | 'desc';

/**
 * Sort tasks by due date.
 * Ascending: earliest dates first, null/undefined dates at the end
 * Descending: latest dates first, null/undefined dates at the end
 * 
 * Requirements: 2.1, 2.2
 */
export function sortByDueDate(
  tasks: TaskWithCase[],
  direction: SortDirection
): TaskWithCase[] {
  return [...tasks].sort((a, b) => {
    // Handle null/undefined due dates - always sort to end
    if (!a.dueDate && !b.dueDate) return 0;
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;
    
    // Compare dates
    const dateA = new Date(a.dueDate).getTime();
    const dateB = new Date(b.dueDate).getTime();
    
    if (direction === 'asc') {
      return dateA - dateB;
    } else {
      return dateB - dateA;
    }
  });
}

/**
 * Sort tasks by student name alphabetically.
 * Ascending: A-Z
 * Descending: Z-A
 * 
 * Requirements: 2.3, 2.4
 */
export function sortByStudentName(
  tasks: TaskWithCase[],
  direction: SortDirection
): TaskWithCase[] {
  return [...tasks].sort((a, b) => {
    const nameA = a.studentName.toLowerCase();
    const nameB = b.studentName.toLowerCase();
    
    const comparison = nameA.localeCompare(nameB);
    
    if (direction === 'asc') {
      return comparison;
    } else {
      return -comparison;
    }
  });
}
